import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import MarkerChips from "./MarkerChips";
import HelpTip from "./HelpTip";
import HELP, { FIELD_HELP } from "./helpText";
import { formatWindow, isWildcardResult } from "./regmFormat";
import {
  DETAIL_SECTIONS,
  formatAttestedAt,
  historyDetail,
  pricingDateLine,
  reviewHistoryLine,
  reviewHistoryView,
  reviewProblems,
  reviewStatusLabel,
  reviewedByLine,
  statusCell,
  verificationLabel,
  verifyLinkLabel,
  HISTORY_FIELDS,
} from "./historyFormat";
import { AMBER_TEXT, GLAZER_GREEN, amberChipSx } from "./regmColors";
import { TOUCH, focusRing } from "./regmStyles";

// Flagged and pending amber, clean and cleared green; neutral (clean but unresolved),
// unavailable and not applicable grey.
const toneChip = (tone) => {
  if (tone === "flagged") return { variant: "filled", sx: amberChipSx };
  if (tone === "clean") return { variant: "outlined", sx: { color: GLAZER_GREEN, borderColor: GLAZER_GREEN } };
  return { variant: "outlined" };
};

const REVIEW_TONES = { pending: "flagged", cleared: "clean" };

const Caption = ({ children }) => (
  <Typography variant="caption" display="block" color="text.secondary">
    {children}
  </Typography>
);

// Cells are plain blocks so the narrow-screen card view (HistoryCard) reuses them outside a table.
export const WhenCell = ({ record }) => (
  <>
    <Typography variant="body2">
      {formatAttestedAt(record.attested_at)}
    </Typography>
    <Caption>{record.user}</Caption>
  </>
);

export const SecurityCell = ({ record }) => (
  <>
    <Typography variant="body2" component="span" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
      {record.security}
    </Typography>
    {isWildcardResult(record) && (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", ml: 1 }}>
        <Chip size="small" variant="outlined" label="Prefix search" />
        <HelpTip text={HELP.prefixSearch} label="prefix search" />
      </Box>
    )}
    {record.isin && <Caption>{record.isin}</Caption>}
  </>
);

export const WindowCell = ({ record }) => (
  <>
    <Typography variant="body2">{formatWindow(record.window_start, record.window_end)}</Typography>
    <Caption>{pricingDateLine(record)}</Caption>
  </>
);

export const StatusCell = ({ record }) => {
  const cell = statusCell(record);
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
      <Chip size="small" label={cell.result.label} {...toneChip(cell.result.tone)} />
      {cell.related && <Chip size="small" label={cell.related.label} {...toneChip(cell.related.tone)} />}
      <MarkerChips badges={cell.markers} help />
    </Stack>
  );
};

// Same strictness as reviewStatusLabel: unknown or missing never looks cleared.
export const ReviewCell = ({ record }) => {
  const label = reviewStatusLabel(record.review_status);
  const line = reviewedByLine(record);
  return (
    <>
      <Chip
        size="small"
        label={label}
        {...toneChip(label === "Unavailable" ? "unavailable" : REVIEW_TONES[record.review_status])}
      />
      {line && <Caption>{line}</Caption>}
    </>
  );
};

export const ActionsCell = ({ record, pdf, onPdf, action, onReview }) => (
  <Stack spacing={0.5} alignItems="flex-start">
    <Stack direction="row" spacing={1} alignItems="center">
      <Tooltip title="Download PDF">
        <span>
          <IconButton
            onClick={() => onPdf(record.id)}
            disabled={pdf.loading}
            aria-label={`Download PDF for ${record.security}`}
            sx={focusRing}
          >
            {pdf.loading ? <CircularProgress size={20} /> : <PictureAsPdfOutlinedIcon />}
          </IconButton>
        </span>
      </Tooltip>
      {action && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onReview(record, action)}
          sx={{
            minHeight: TOUCH,
            textTransform: "none",
            color: action === "clear" ? GLAZER_GREEN : AMBER_TEXT,
            borderColor: action === "clear" ? GLAZER_GREEN : AMBER_TEXT,
            "&:hover": { borderColor: action === "clear" ? GLAZER_GREEN : AMBER_TEXT },
            ...focusRing,
          }}
        >
          {action === "clear" ? "Clear" : "Reopen"}
        </Button>
      )}
      {action && <HelpTip text={HELP.reviewAction} label="Clear and Reopen" />}
    </Stack>
    {pdf.error && (
      <Typography variant="caption" color="error" role="alert">
        {pdf.error}
      </Typography>
    )}
  </Stack>
);

const chainText = (ok) => (ok === true ? "intact" : ok === false ? "broken" : "unknown");

export const VerifyBlock = ({ verify, onVerify }) => {
  const label = verify.result ? verificationLabel(verify.result) : null;
  return (
    <Stack spacing={0.5} alignItems="flex-start" aria-live="polite">
      {label && (
        <Box sx={{ fontWeight: 700, color: label === "VERIFIED" ? GLAZER_GREEN : "error.main" }}>
          {label}
          <Caption>Chain: {chainText(verify.result.chain_ok)}</Caption>
          {reviewProblems(verify.result).map((problem, i) => (
            <Typography key={i} variant="caption" display="block" color="error">
              {problem}
            </Typography>
          ))}
        </Box>
      )}
      {verify.error && (
        <Typography variant="caption" color="error">
          {verify.error}
        </Typography>
      )}
      <Link
        component="button"
        type="button"
        variant="body2"
        onClick={onVerify}
        disabled={verify.loading}
        sx={{ minHeight: TOUCH, ...focusRing, "&:disabled": { color: "text.disabled", cursor: "default" } }}
      >
        {verifyLinkLabel(verify)}
      </Link>
    </Stack>
  );
};

// Quiet: loading and failure are plain secondary text with a Retry link, never an error banner.
const ReviewHistory = ({ view, onRetry }) => {
  if (view.kind === "list") {
    return view.items.map((rv, i) => <Caption key={rv?.id ?? i}>{reviewHistoryLine(rv)}</Caption>);
  }
  if (view.kind === "none") return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
      <Typography variant="body2" color="text.secondary">
        {view.text}
      </Typography>
      {view.retry && (
        <Link component="button" type="button" variant="body2" onClick={onRetry} sx={{ minHeight: TOUCH, ...focusRing }}>
          Retry
        </Link>
      )}
    </Stack>
  );
};

// Expanded content in DETAIL_SECTIONS order.
export const HistoryDetail = ({ record, reviews, verify, onVerify, onRetryReviews }) => {
  const detail = historyDetail(record);
  const body = {
    Reasons:
      detail.reasons.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None recorded
        </Typography>
      ) : (
        detail.reasons.map((r, i) => (
          <Typography key={i} variant="body2" sx={{ color: record.flagged === true ? AMBER_TEXT : undefined }}>
            {r}
          </Typography>
        ))
      ),
    Markers: <MarkerChips badges={detail.markers} help />,
    Signature: (
      <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
        {detail.signature}
      </Typography>
    ),
    "Attestation version": <Typography variant="body2">{detail.attestationVersion}</Typography>,
    "Review history": <ReviewHistory view={reviewHistoryView(reviews)} onRetry={onRetryReviews} />,
    Verify: <VerifyBlock verify={verify} onVerify={onVerify} />,
  };
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
        gap: 2,
        py: 2,
      }}
    >
      {DETAIL_SECTIONS.map((title) => (
        <Box key={title} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" component="h4" sx={{ mb: 0.5 }}>
            {title}
            {title === "Verify" && <HelpTip text={HELP.verify} label="verify" />}
          </Typography>
          {body[title]}
        </Box>
      ))}
    </Box>
  );
};

// Column label (table header and card row label) with its info tooltip when it has one.
export const FieldLabel = ({ field, label }) => (
  <>
    {label}
    {FIELD_HELP[field] && <HelpTip text={FIELD_HELP[field]} label={label} spaced />}
  </>
);

export const COLUMN_COUNT = HISTORY_FIELDS.length;

// One field block by HISTORY_FIELDS key; the table and the card both render through this.
export const FieldBlock = ({ field, record, pdf, onPdf, action, onReview }) => {
  switch (field) {
    case "when":
      return <WhenCell record={record} />;
    case "security":
      return <SecurityCell record={record} />;
    case "window":
      return <WindowCell record={record} />;
    case "status":
      return <StatusCell record={record} />;
    case "review":
      return <ReviewCell record={record} />;
    case "actions":
      return <ActionsCell record={record} pdf={pdf} onPdf={onPdf} action={action} onReview={onReview} />;
    default:
      return null;
  }
};

export const detailIdFor = (record) => `regm-history-detail-${record.id}`;

export const ToggleArrow = ({ open, reducedMotion }) => (
  <KeyboardArrowDownIcon
    sx={{
      transform: open ? "none" : "rotate(-90deg)",
      transition: reducedMotion ? "none" : "transform 150ms",
    }}
  />
);

const HistoryRow = ({
  record,
  open,
  onToggle,
  verify,
  onVerify,
  reviews,
  onRetryReviews,
  pdf,
  onPdf,
  action,
  onReview,
}) => {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", { noSsr: true });
  const toggleLabel = `${open ? "Hide" : "Show"} details for ${record.security}`;
  const detailId = detailIdFor(record);
  const top = { verticalAlign: "top" };
  return (
    <>
      <TableRow sx={{ "& > td": { borderBottom: open ? "none" : undefined } }}>
        {HISTORY_FIELDS.map(({ key }) => (
          <TableCell key={key} sx={top}>
            {key === "when" ? (
              <Stack direction="row" spacing={0.5} alignItems="flex-start">
                <Tooltip title={open ? "Hide details" : "Show details"}>
                  <IconButton
                    size="small"
                    onClick={() => onToggle(record.id)}
                    aria-expanded={open}
                    aria-controls={detailId}
                    aria-label={toggleLabel}
                    sx={{ width: TOUCH, height: TOUCH, mt: -1, ...focusRing }}
                  >
                    <ToggleArrow open={open} reducedMotion={reducedMotion} />
                  </IconButton>
                </Tooltip>
                <Box>
                  <FieldBlock field="when" record={record} />
                </Box>
              </Stack>
            ) : (
              <FieldBlock field={key} record={record} pdf={pdf} onPdf={onPdf} action={action} onReview={onReview} />
            )}
          </TableCell>
        ))}
      </TableRow>
      <TableRow id={detailId}>
        <TableCell
          colSpan={COLUMN_COUNT}
          sx={{ py: 0, pl: 7, borderBottom: open ? undefined : "none", bgcolor: open ? "action.hover" : undefined }}
        >
          <Collapse in={open} timeout={reducedMotion ? 0 : "auto"} unmountOnExit>
            <HistoryDetail
              record={record}
              reviews={reviews}
              verify={verify}
              onVerify={onVerify}
              onRetryReviews={onRetryReviews}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default HistoryRow;
